import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, OnDestroy, OnChanges, SimpleChanges, Output, ChangeDetectorRef } from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSnackBar } from '@angular/material/snack-bar';
import { WarehouseAttributes } from '@albatrosdeveloper/ave-models-package';
import { FormSectionComponent } from 'app/shared/components/form-ui/form-section/form-section.component';
import { ToggleOptionComponent } from 'app/shared/components/form-ui/toggle-option/toggle-option.component';
import { ReplaySubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { WarehouseService } from 'app/modules/components/configuration/warehouse/warehouse.service';

export interface IWarehouseAssignmentData {
  warehouseIds: string[];
  availableInAllWarehouses: boolean;
}

@Component({
  selector: 'app-material-warehouse-assignment',
  standalone: true,
  templateUrl: './material-warehouse-assignment.component.html',
  styleUrls: ['./material-warehouse-assignment.component.scss'],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatSelectModule,
    MatInputModule,
    MatCheckboxModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    FormSectionComponent,
    ToggleOptionComponent,
  ],
})
export class MaterialWarehouseAssignmentComponent implements OnInit, OnChanges, OnDestroy {
  @Input() selectedWarehouses: string[] = [];
  @Input() availableInAllWarehouses: boolean = false;
  @Input() plantId: string | undefined = undefined;
  @Output() warehousesChange = new EventEmitter<IWarehouseAssignmentData>();
  @Output() formValidityChange = new EventEmitter<boolean>();

  warehouseMultiCtrl = new FormControl<WarehouseAttributes[]>([], Validators.required);
  warehouseFilterCtrl = new FormControl<string>('');
  availableInAllCtrl = new FormControl<boolean>(false);

  warehouseList: WarehouseAttributes[] = [];
  filteredWarehouses: ReplaySubject<WarehouseAttributes[]> = new ReplaySubject<WarehouseAttributes[]>(1);
  private currentFilteredWarehouses: WarehouseAttributes[] = [];

  private _onDestroy = new Subject<void>();
  private isInitializing: boolean = true;

  constructor(
    private readonly warehouseService: WarehouseService,
    private snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.availableInAllCtrl.setValue(this.availableInAllWarehouses);
    this.fetchWarehouses();
    this.setupFilterListener();
    this.setupValueChangeListeners();

    setTimeout(() => {
      this.isInitializing = false;
      this.emitFormValidity();
    }, 300);
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Detectar cambios en plantId y recargar warehouses
    if (changes['plantId'] && !changes['plantId'].firstChange) {
      this.fetchWarehouses();
    }

    // Detectar cambios en availableInAllWarehouses
    if (changes['availableInAllWarehouses'] && !changes['availableInAllWarehouses'].firstChange) {
      this.availableInAllCtrl.setValue(this.availableInAllWarehouses, { emitEvent: false });
    }

    // Detectar cambios en selectedWarehouses después de que la lista se haya cargado
    if (changes['selectedWarehouses'] && !changes['selectedWarehouses'].firstChange && this.warehouseList.length > 0) {
      this.updateSelectedWarehouses();
    }
  }

  private fetchWarehouses(): void {
    this.warehouseService.findToComboBox(this.plantId).subscribe({
      next: (res) => {
        this.warehouseList = res.data || [];
        this.filteredWarehouses.next(this.warehouseList.slice());

        // Actualizar los warehouses seleccionados después de cargar la lista
        this.updateSelectedWarehouses();
      },
      error: (err) => {
        this.snackBar.open(
          err.error.message || 'Error al obtener almacenes',
          'Cerrar',
          { duration: 5000 }
        );
      },
    });
  }

  private updateSelectedWarehouses(): void {
    if (this.selectedWarehouses && this.selectedWarehouses.length > 0 && this.warehouseList.length > 0) {
      const selectedWarehouseObjects = this.warehouseList.filter((w) =>
        this.selectedWarehouses.includes(w._id.toString())
      );
      this.warehouseMultiCtrl.setValue(selectedWarehouseObjects, { emitEvent: false });
    }
  }

  private setupFilterListener(): void {
    this.warehouseFilterCtrl.valueChanges
      .pipe(takeUntil(this._onDestroy))
      .subscribe((searchTerm) => {
        this.filterWarehouses(searchTerm || '');
      });

    // Suscribirse a los cambios del filtro para actualizar la lista actual
    this.filteredWarehouses
      .pipe(takeUntil(this._onDestroy))
      .subscribe((filtered) => {
        this.currentFilteredWarehouses = filtered;
      });
  }

  private setupValueChangeListeners(): void {
    this.warehouseMultiCtrl.valueChanges
      .pipe(takeUntil(this._onDestroy))
      .subscribe(() => {
        if (!this.isInitializing) {
          this.emitWarehouseData();
          this.emitFormValidity();
        }
      });

    this.availableInAllCtrl.valueChanges
      .pipe(takeUntil(this._onDestroy))
      .subscribe((allWarehouses) => {
        if (allWarehouses) {
          this.warehouseMultiCtrl.clearValidators();
          this.warehouseMultiCtrl.disable();
        } else {
          this.warehouseMultiCtrl.setValidators(Validators.required);
          this.warehouseMultiCtrl.enable();
        }
        this.warehouseMultiCtrl.updateValueAndValidity();

        if (!this.isInitializing) {
          this.emitWarehouseData();
          this.emitFormValidity();
        }
      });
  }

  private filterWarehouses(search: string): void {
    if (!search) {
      this.filteredWarehouses.next(this.warehouseList.slice());
      return;
    }

    const searchLower = search.toLowerCase();
    const filtered = this.warehouseList.filter((w) => {
      const nameMatches = w.name.toLowerCase().includes(searchLower);
      const codeMatches = w.externalKeys?.code?.toLowerCase().includes(searchLower);
      return nameMatches || codeMatches;
    });
    this.filteredWarehouses.next(filtered);
  }

  private emitWarehouseData(): void {
    const data: IWarehouseAssignmentData = {
      warehouseIds: this.warehouseMultiCtrl.value?.map((w) => w._id.toString()) || [],
      availableInAllWarehouses: this.availableInAllCtrl.value || false,
    };
    this.warehousesChange.emit(data);
  }

  private emitFormValidity(): void {
    const isValid = this.isFormValid();
    this.formValidityChange.emit(isValid);
  }

  isFormValid(): boolean {
    if (this.isInitializing) {
      return true;
    }

    const allWarehousesSelected = this.availableInAllCtrl.value;
    if (allWarehousesSelected) {
      return true;
    }

    return this.warehouseMultiCtrl.valid && this.warehouseMultiCtrl.value?.length > 0;
  }

  onSearchInputClick(event: Event): void {
    event.stopPropagation();
  }

  onSearchInputKeydown(event: Event): void {
    event.stopPropagation();
  }

  clearSelection(): void {
    this.warehouseMultiCtrl.setValue([]);
    this.warehouseFilterCtrl.setValue('');
  }

  // Métodos para trabajar con los almacenes FILTRADOS actualmente
  isAllFilteredSelected(): boolean {
    const selectedWarehouses = this.warehouseMultiCtrl.value || [];
    if (this.currentFilteredWarehouses.length === 0) return false;

    return this.currentFilteredWarehouses.every(warehouse =>
      selectedWarehouses.some(selected => selected._id === warehouse._id)
    );
  }

  isSomeFilteredSelected(): boolean {
    const selectedWarehouses = this.warehouseMultiCtrl.value || [];
    if (this.currentFilteredWarehouses.length === 0) return false;

    const selectedCount = this.currentFilteredWarehouses.filter(warehouse =>
      selectedWarehouses.some(selected => selected._id === warehouse._id)
    ).length;

    return selectedCount > 0 && selectedCount < this.currentFilteredWarehouses.length;
  }

  toggleSelectAllFiltered(): void {
    const selectedWarehouses = this.warehouseMultiCtrl.value || [];

    if (this.isAllFilteredSelected()) {
      // Deseleccionar todos los filtrados
      const newSelection = selectedWarehouses.filter(selected =>
        !this.currentFilteredWarehouses.some(filtered => filtered._id === selected._id)
      );
      this.warehouseMultiCtrl.setValue(newSelection);
    } else {
      // Seleccionar todos los filtrados
      const currentIds = selectedWarehouses.map(w => w._id);
      const toAdd = this.currentFilteredWarehouses.filter(
        warehouse => !currentIds.includes(warehouse._id)
      );
      this.warehouseMultiCtrl.setValue([...selectedWarehouses, ...toAdd]);
    }
  }

  getFilteredCount(): number {
    return this.currentFilteredWarehouses.length;
  }

  getSelectedFilteredCount(): number {
    const selectedWarehouses = this.warehouseMultiCtrl.value || [];
    return this.currentFilteredWarehouses.filter(warehouse =>
      selectedWarehouses.some(selected => selected._id === warehouse._id)
    ).length;
  }

  ngOnDestroy(): void {
    this._onDestroy.next();
    this._onDestroy.complete();
  }
}
